/**
 * Camada de acesso a dados (repositórios). Funções de leitura pensadas para uso
 * com `useLiveQuery` (dexie-react-hooks) e funções de escrita que respeitam as
 * regras do escopo: origem gravada sempre, ofertas da criança nascem pendentes.
 *
 * Nota sobre `confirmado`: o IndexedDB não indexa booleanos, então filtramos
 * pendências em memória (a base local é pequena — milhares de ofertas no máximo).
 */
import { db } from "../schema.dexie";
import { ofertaDaCrianca } from "../domain";
import type {
  Alimento,
  Config,
  Crianca,
  EspecialidadeProf,
  GrupoAlimentar,
  Oferta,
  PassoEscada,
  PropriedadesSensoriais,
  Refeicao,
  SistemaCAA,
  StatusAlimento,
} from "../types";
import { PassoEscada as Passo } from "../types";

const novoId = () => crypto.randomUUID();
const agoraISO = () => new Date().toISOString();

// ── Leituras (use com useLiveQuery) ─────────────────────────────────────────

export const getConfig = () => db.config.get("singleton") as Promise<
  (Config & { id: string }) | undefined
>;

export const getCriancaAtiva = async (): Promise<Crianca | undefined> => {
  const cfg = await db.config.get("singleton");
  if (!cfg?.crianca_ativa_id) return undefined;
  return db.crianca.get(cfg.crianca_ativa_id);
};

/** Alimentos não arquivados, ordenados por nome. */
export const listarAlimentos = (incluirArquivados = false): Promise<Alimento[]> =>
  db.alimento
    .orderBy("nome")
    .filter((a) => incluirArquivados || !a.arquivado)
    .toArray();

export const getAlimento = (id: string) => db.alimento.get(id);

export const getTodasOfertas = (): Promise<Oferta[]> => db.oferta.toArray();

export const getOfertasDoAlimento = (alimentoId: string): Promise<Oferta[]> =>
  db.oferta
    .where("[alimento_id+data_hora]")
    .between([alimentoId, ""], [alimentoId, "￿"])
    .reverse()
    .toArray();

/** Ofertas pendentes de confirmação (origem criança). Filtra em memória. */
export const getPendentes = (): Promise<Oferta[]> =>
  db.oferta.filter((o) => o.confirmado === false).toArray();

// ── Escritas ─────────────────────────────────────────────────────────────────

export type NovaOfertaInput = {
  alimento_id: string;
  crianca_id: string;
  passo_escada: PassoEscada;
  porcao_comida_pct?: number | null;
  porcao_oferecida?: string | null;
  refeicao?: Oferta["refeicao"];
  local?: string | null;
  quem_ofereceu?: string | null;
  quem_presente?: string[];
  humor_antes?: Oferta["humor_antes"];
  notas?: string | null;
};

/** P0.2 — registro do cuidador: origem fixa, confirmado, em ≤10s. */
export async function registrarOfertaCuidador(input: NovaOfertaInput): Promise<string> {
  const oferta: Oferta = {
    id: novoId(),
    data_hora: agoraISO(),
    porcao_comida_pct: null,
    porcao_oferecida: null,
    refeicao: null,
    local: null,
    quem_ofereceu: null,
    humor_antes: null,
    notas: null,
    ...input,
    quem_presente: input.quem_presente ?? [],
    origem: "cuidador",
    confirmado: true,
  };
  await db.oferta.add(oferta);
  return oferta.id;
}

/**
 * Registro rápido de recusa pelo cuidador: 1 toque, sem abrir o fluxo completo.
 * Grava como oferta passo 0 ("não tolera na mesa") — a recusa honesta na escada —
 * com origem cuidador. É o dado negativo de casa pra confrontar com a terapia.
 */
export function registrarRecusa(alimentoId: string, criancaId: string): Promise<string> {
  return registrarOfertaCuidador({
    alimento_id: alimentoId,
    crianca_id: criancaId,
    passo_escada: Passo.NaoToleraNaMesa,
    notas: "recusou (registro rápido)",
  });
}

export type OfertaProfissionalInput = NovaOfertaInput & {
  profissional_nome?: string | null;
  profissional_especialidade?: EspecialidadeProf | null;
};

/**
 * Registro de uma sessão de terapia: origem `profissional`, confirmado.
 * Reusa a mesma escada — assim casa e terapia ficam comparáveis na timeline.
 */
export async function registrarOfertaProfissional(
  input: OfertaProfissionalInput,
): Promise<string> {
  const { profissional_nome, profissional_especialidade, ...resto } = input;
  const oferta: Oferta = {
    id: novoId(),
    data_hora: agoraISO(),
    porcao_comida_pct: null,
    porcao_oferecida: null,
    refeicao: null,
    local: null,
    quem_ofereceu: null,
    humor_antes: null,
    notas: null,
    ...resto,
    quem_presente: resto.quem_presente ?? [],
    origem: "profissional",
    confirmado: true,
    profissional_nome: profissional_nome?.trim() || null,
    profissional_especialidade: profissional_especialidade ?? null,
  };
  await db.oferta.add(oferta);
  return oferta.id;
}

/** P0.3 — escolha da criança: origem `crianca`, nasce pendente (confirmado=false). */
export async function registrarEscolhaCrianca(
  alimentoId: string,
  criancaId: string,
  passo: PassoEscada,
  refeicao: Refeicao | null = null,
): Promise<string> {
  const base = {
    id: novoId(),
    alimento_id: alimentoId,
    crianca_id: criancaId,
    data_hora: agoraISO(),
    passo_escada: passo,
    porcao_comida_pct: null,
    porcao_oferecida: null,
    refeicao,
    local: null,
    quem_ofereceu: null,
    quem_presente: [] as string[],
    humor_antes: null,
    notas: "escolheu no modo escolha",
  };
  const oferta = ofertaDaCrianca(base);
  await db.oferta.add(oferta);
  return oferta.id;
}

export const confirmarOferta = (id: string) =>
  db.oferta.update(id, { confirmado: true });

export const descartarOferta = (id: string) => db.oferta.delete(id);

// ── Biblioteca (P0.1) ────────────────────────────────────────────────────────

export type NovoAlimentoInput = {
  nome: string;
  marca_produto?: string | null;
  categoria?: string | null;
  grupo?: GrupoAlimentar | null;
  sensorial?: Partial<PropriedadesSensoriais>;
  status?: StatusAlimento;
  observacoes?: string | null;
  fotos?: string[];
};

const SENSORIAL_VAZIO: PropriedadesSensoriais = {
  textura: null,
  temperatura: null,
  cor: null,
  forma_apresentacao: null,
  cheiro: null,
};

export async function criarAlimento(input: NovoAlimentoInput): Promise<string> {
  const alimento: Alimento = {
    id: novoId(),
    nome: input.nome.trim(),
    fotos: input.fotos ?? [],
    marca_produto: input.marca_produto ?? null,
    categoria: input.categoria ?? null,
    grupo: input.grupo ?? null,
    sensorial: { ...SENSORIAL_VAZIO, ...input.sensorial },
    status: input.status ?? "nao_oferecido",
    observacoes: input.observacoes ?? null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.alimento.add(alimento);
  return alimento.id;
}

export const editarAlimento = (id: string, patch: Partial<Alimento>) =>
  db.alimento.update(id, patch);

export const definirStatus = (id: string, status: StatusAlimento) =>
  db.alimento.update(id, { status });

/** P0.1 — arquivar em vez de deletar. */
export const arquivarAlimento = (id: string) =>
  db.alimento.update(id, { arquivado: true });

// ── Config (P0.6 / criança ativa) ────────────────────────────────────────────

export async function salvarConfig(patch: Partial<Config>): Promise<void> {
  const atual = await db.config.get("singleton");
  await db.config.put({ id: "singleton", ...(atual as Config), ...patch });
}

// ── Paciente + responsável (cadastro básico) ─────────────────────────────────

export type PacienteInput = {
  apelido?: string;
  nome_completo?: string | null;
  data_nascimento?: string | null;
  sistema_caa?: SistemaCAA;
  responsavel_nome?: string | null;
  responsavel_cpf?: string | null;
  recomendacoes?: string | null;
};

/**
 * Cria (na 1ª vez, virando o paciente ativo) ou atualiza o paciente ativo.
 * Dados ficam só no aparelho — sem conta, sem nuvem.
 */
export async function salvarPaciente(patch: PacienteInput): Promise<string> {
  const cfg = await db.config.get("singleton");
  const ativoId = cfg?.crianca_ativa_id ?? undefined;
  if (ativoId && (await db.crianca.get(ativoId))) {
    await db.crianca.update(ativoId, patch);
    return ativoId;
  }

  const id = novoId();
  const crianca: Crianca = {
    id,
    apelido: patch.apelido?.trim() || "paciente",
    nome_completo: patch.nome_completo ?? null,
    data_nascimento: patch.data_nascimento ?? null,
    foto: null,
    sistema_caa: patch.sistema_caa ?? "nenhum",
    responsavel_nome: patch.responsavel_nome ?? null,
    responsavel_cpf: patch.responsavel_cpf ?? null,
    recomendacoes: patch.recomendacoes ?? null,
    criado_em: agoraISO(),
  };
  await db.crianca.add(crianca);
  await salvarConfig({ crianca_ativa_id: id });
  return id;
}

/**
 * Cria um NOVO paciente (sempre adiciona; nunca sobrescreve o ativo) e o torna o
 * paciente ativo. É o fluxo multi-paciente do profissional — distinto de
 * `salvarPaciente`, que edita o paciente ativo (o "Meu Perfil" da família).
 */
export async function criarNovoPaciente(patch: PacienteInput): Promise<string> {
  const id = novoId();
  const crianca: Crianca = {
    id,
    apelido: patch.apelido?.trim() || "paciente",
    nome_completo: patch.nome_completo ?? null,
    data_nascimento: patch.data_nascimento ?? null,
    foto: null,
    sistema_caa: patch.sistema_caa ?? "nenhum",
    responsavel_nome: patch.responsavel_nome ?? null,
    responsavel_cpf: patch.responsavel_cpf ?? null,
    recomendacoes: patch.recomendacoes ?? null,
    criado_em: agoraISO(),
  };
  await db.crianca.add(crianca);
  await salvarConfig({ crianca_ativa_id: id });
  return id;
}
